import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IncomingCallService } from '../../../shared/services/incoming-call.service';
import { IncomingCallData } from '../../../shared/models/incoming-call.model';
import { SocketService } from '../../../services/socket.service';
import { MissedCallService } from '../../../shared/services/missed-call.service';

@Component({
  selector: 'app-incoming-call',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './incoming-call.component.html',
  styleUrls: ['./incoming-call.component.scss'],
})
export class IncomingCallComponent implements OnInit, OnDestroy {
  @ViewChild('ringingAudio') ringingAudio!: ElementRef<HTMLAudioElement>;

  callerName = 'Patient';
  roomName = '';
  callType: 'audio' | 'video' = 'video';
  showIncomingCall = false;
  consultationData: any = null;
  autoDisconnectSeconds = 45;
  private incomingCallSubscription!: Subscription;
  private countdownInterval: any;

  constructor(
    private router: Router,
    private incomingCallService: IncomingCallService,
    private socketService: SocketService,
    private missedCallService: MissedCallService,
  ) {}

  ngOnInit() {
    this.incomingCallSubscription =
      this.incomingCallService.incomingCall$.subscribe(
        (data: IncomingCallData | null) => {
          if (data) {
            this.roomName = data.roomName;
            this.callerName = data.callerName;
            this.callType = data.callType;
            this.consultationData = data.consultationData || null;
            this.showIncomingCall = true;
            this.autoDisconnectSeconds = 45;
            this.startCountdown();
            setTimeout(() => {
              this.playRinging();
            }, 500);
          } else {
            this.showIncomingCall = false;
            this.stopRinging();
            this.stopCountdown();
          }
        },
      );
  }

  get doctorName(): string {
    const doctor = this.consultationData?.data?.doctor;

    return [doctor?.first_name, doctor?.last_name]
      .filter((name) => !!name)
      .join(' ');
  }

  private startCountdown() {
    this.stopCountdown();
    this.countdownInterval = setInterval(() => {
      this.autoDisconnectSeconds--;
      if (this.autoDisconnectSeconds <= 0) {
        this.stopCountdown();
        this.onCallTimeout();
      }
    }, 1000);
  }

  private onCallTimeout() {
    const doctor = this.consultationData?.data?.doctor;
    const doctorName = [doctor?.first_name, doctor?.last_name]
      .filter(Boolean)
      .join(' ');
    const bookingId =
      this.consultationData?.tc_booking_id ||
      this.consultationData?.data?.tc_booking_id ||
      '';

    this.missedCallService.recordMissedCall({
      bookingId,
      doctorName,
      callType: this.callType,
      timestamp: Date.now(),
    });

    this.stopRinging();
    this.incomingCallService.hideIncomingCall();
  }

  private stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  playRinging() {
    if (this.ringingAudio) {
      this.ringingAudio.nativeElement.play().catch((err) => {
        this.setupAudioOnInteraction();
      });
    }
  }

  private setupAudioOnInteraction() {
    const playAudio = () => {
      if (this.ringingAudio) {
        this.ringingAudio.nativeElement
          .play()
          .catch((err) => console.log('Still failed:', err));
      }
      document.removeEventListener('click', playAudio);
      document.removeEventListener('touchstart', playAudio);
    };

    document.addEventListener('click', playAudio, { once: true });
    document.addEventListener('touchstart', playAudio, { once: true });
  }

  ngOnDestroy() {
    this.incomingCallSubscription.unsubscribe();
    if (this.ringingAudio) {
      this.ringingAudio.nativeElement.pause();
      this.ringingAudio.nativeElement.currentTime = 0;
    }
  }

  acceptCall() {
    this.stopRinging();
    this.incomingCallService.hideIncomingCall();

    let finalConsultationData = this.consultationData;

    if (!finalConsultationData?.tc_booking_id) {
      try {
        const storedData = JSON.parse(
          localStorage.getItem('consultationData') || 'null',
        );
        if (storedData?.tc_booking_id) {
          finalConsultationData = { ...finalConsultationData, ...storedData };
        }
      } catch (error) {
        console.error(
          'Failed to parse consultationData from localStorage:',
          error,
        );
      }
    }

    const route =
      this.callType === 'audio'
        ? '/patients/audio-room'
        : '/patients/video-room';
    this.router.navigate([route, this.roomName], {
      state: { consultationData: finalConsultationData },
    });
  }

  declineCall() {
    this.stopRinging();
    this.incomingCallService.hideIncomingCall();

    this.socketService.emit('call_declined', {
      roomName: this.roomName,
      callType: this.callType,
      reason: 'Doctor declined the call',
    });
  }

  private stopRinging() {
    if (this.ringingAudio) {
      this.ringingAudio.nativeElement.pause();
      this.ringingAudio.nativeElement.currentTime = 0;
    }
  }
}
